import { getQueryParam } from '@vfde-sails/utils';
import { getCMSConfig, getTariffIds } from '../../../util/cms';
import { AppState } from '../interface';
import { STORAGE_KEY } from '../../../constants';
import { debugInfo } from '../../../util/debug';
import { isValidId } from '../../../util/validator';
import { initialState } from '../reducer';
import { State } from '../index';
import { QueryNames } from '../../../enums/network';
import { OverlayContent } from '../../../enums/elementId';
import { getSessionStorageItemJson, setSessionStorageItemJson } from '@vfde-sails/storage';
import { getAddressFromCookie } from '../../../util/cookieHelper';
import { SessionAppState } from '../interface';
import { ProductType } from '../../../enums/product';

const getStateFromPageOptions = (draftState: AppState) => {
    const config = getCMSConfig();

    if (!config) {
        return draftState;
    }

    const { selected } = config;

    if (selected) {
        draftState.selectedTariff.tariffId = selected.tariffId;
        draftState.selectedTariff.tariff = undefined;
    }

    return draftState;
};

export const saveStateToSession = (state: AppState) => {
    const sessionAppState: SessionAppState = {
        [ProductType.PRE_FIBER]: {
            selectedTariff: { tariffId: state.selectedTariff.tariffId },
        },
    };

    setSessionStorageItemJson(STORAGE_KEY, sessionAppState);
};

const getStateFromSession = (draftState: AppState) => {
    const sessionState: SessionAppState | null = getSessionStorageItemJson(STORAGE_KEY);
    const appState: Partial<AppState> | undefined = sessionState?.[ProductType.PRE_FIBER];

    if (!appState) {
        debugInfo('Failed to load state from SessionStorage');
        return draftState;
    }

    return { ...draftState, ...appState };
};

const getStateFromQuery = (draftState: AppState) => {
    const tariffId = getQueryParam(QueryNames.TARIFF);

    if (isValidId(tariffId, getTariffIds())) {
        draftState.selectedTariff.tariffId = tariffId as string;
        draftState.selectedTariff.tariff = draftState.tariffs
            .find(tariff => tariff.id === tariffId);
    }

    return draftState;
};

const getStateFromCookie = (draftState: AppState) => {

    try {
        draftState.address = getAddressFromCookie();
    } catch (error) {
        draftState.address = undefined;
        draftState.avcResult = undefined;
    }

    return draftState;
};

/**
 * Load state from sessionStorage, additionalPageOptions, query params, cookies.
 *
 * @returns AppState
 */
export const loadInitialAppState = (): Partial<AppState> => {
    // we need a deep copy of initialState because 'immer' makes it read-only
    let draftState: AppState = JSON.parse(JSON.stringify(initialState));

    draftState = getStateFromPageOptions(draftState);
    draftState = getStateFromSession(draftState);
    draftState = getStateFromQuery(draftState);
    draftState = getStateFromCookie(draftState);

    return draftState;
};

/* istanbul ignore next */
export const getUpdateFlags = (state: State, oldState: State) => {
    const {
        isLoading,
        activeOverlay,
        isOverlayLoaded,
        avcResult,
        selected,
        selectedTariff,
        isOverlayOpen,
        tariffs,
        accessSupplier,
        availabilityIndicator,
        homesPassed,
    } = state;
    const avcResultChanged = oldState.avcResult !== avcResult;
    const tariffChanged = selected.tariffId && selected.tariffId !== oldState.selected.tariffId;
    const overlayOpened = isOverlayOpen && oldState.isOverlayOpen !== isOverlayOpen;
    const tariffsFirstLoad = !oldState.tariffs?.length && !!tariffs?.length;
    const priceChanged = oldState.selected.tariff?.discount !== selected.tariff?.discount;
    const accessSupplierChanged = accessSupplier !== oldState?.accessSupplier;
    const availabilityIndicatorChanged = availabilityIndicator !== oldState?.availabilityIndicator;
    const homesPassedChanged = homesPassed !== oldState?.homesPassed;

    return {
        isLoading: isLoading !== oldState.isLoading,
        overlay: isOverlayLoaded && oldState.isOverlayLoaded !== isOverlayLoaded,
        footnote: avcResultChanged || tariffChanged || tariffsFirstLoad || priceChanged,
        avcOpened: selectedTariff && overlayOpened && activeOverlay === OverlayContent.FORM,
        overlayChanged: activeOverlay !== oldState.activeOverlay,
        tariffChanged,
        avcResultChanged: avcResultChanged || accessSupplierChanged || availabilityIndicatorChanged,
        promotionalCard:
            avcResultChanged ||
            accessSupplierChanged ||
            availabilityIndicatorChanged ||
            homesPassedChanged,
    };
};
